import React from 'react';
import { NavLink, type NavLinkProps } from 'react-router-dom';

export const dashboardIconClass = 'h-5 w-5 text-gray-500 dark:text-slate-400';

const linkBase =
  'flex items-center gap-3 rounded px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500';

const inactiveLink =
  'text-gray-700 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white';

const activePrimary = 'bg-gray-900 text-white dark:bg-slate-100 dark:text-slate-950';
const activeSection = 'bg-purple-100 text-purple-800 dark:bg-purple-500/20 dark:text-purple-200';

interface DashboardNavLinkProps {
  to: NavLinkProps['to'];
  end?: boolean;
  children: React.ReactNode;
  activeTone?: 'primary' | 'section';
}

export const DashboardNavLink: React.FC<DashboardNavLinkProps> = ({
  to,
  end,
  children,
  activeTone = 'section',
}) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) =>
      `${linkBase} ${isActive ? (activeTone === 'primary' ? activePrimary : activeSection) : inactiveLink}`
    }
  >
    {children}
  </NavLink>
);

interface DashboardNavButtonProps {
  open: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}

export const DashboardNavButton: React.FC<DashboardNavButtonProps> = ({ open, onClick, icon, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`${linkBase} w-full justify-between ${inactiveLink}`}
    aria-expanded={open}
  >
    <span className="flex items-center gap-3">
      {icon}
      <span>{children}</span>
    </span>
    <svg
      aria-hidden="true"
      className={`h-4 w-4 text-gray-500 transition-transform dark:text-slate-400 ${open ? 'rotate-180' : ''}`}
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z"
        clipRule="evenodd"
      />
    </svg>
  </button>
);

export const DashboardNavSubList: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ul className="mt-1 ml-3 space-y-1 border-l border-gray-200 pl-3 dark:border-slate-700">{children}</ul>
);

export const DashboardNavSectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-fg-subtle">
    {children}
  </div>
);

export const DashboardNavBadge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-gray-200 text-[10px] font-bold text-gray-700 dark:bg-slate-700 dark:text-slate-200">
    {children}
  </span>
);
